import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { YARNS } from "../lib/yarns";

/**
 * OutputCard — the resolved card after a thread pull.
 *
 * Member-mode: no email capture. Each card can be saved/pinned to the
 * member's loomboard (browser-stored for v1). After three pulls in a single
 * day, a quiet daily-rhythm note appears; pulls remain always available.
 */
export default function OutputCard({
  card,
  dailyPullCount,
  guestCapped = false,
  onPullAgain,
  onToggleSave,
  isSaved = false,
}) {
  const reducedMotion = useReducedMotion();
  const [justSaved, setJustSaved] = useState(false);

  if (!card) return null;

  const yarn = YARNS[card.yarn];
  const dailyResting = dailyPullCount >= 3;

  function handleSaveClick() {
    onToggleSave?.(card);
    if (!isSaved) {
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1400);
    }
  }

  function handlePullAgainClick() {
    if (guestCapped) {
      // Scroll up to the paywall instead of attempting a blocked pull
      const paywall = document.querySelector('[data-testid="paywall-section"]');
      if (paywall) {
        paywall.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    onPullAgain?.();
  }

  return (
    <AnimatePresence mode="wait">
      <motion.article
        key={card.id}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 }}
        animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -10, scale: 0.98 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-xl mx-auto rounded-2xl shadow-[0_10px_40px_rgba(60,40,10,0.12)] border-2 overflow-hidden"
        style={{ backgroundColor: yarn.pastel, borderColor: "#3d2a14" }}
        data-testid={`output-card-${card.yarn}`}
      >
        <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: yarn.deep }} />

        {/* Save / pin */}
        <button
          type="button"
          onClick={handleSaveClick}
          aria-label={isSaved ? "Remove from your loomboard" : "Save this thread to your loomboard"}
          aria-pressed={isSaved}
          data-testid="output-card-save-button"
          className="absolute top-4 right-4 inline-flex items-center gap-1.5 pl-3 pr-3.5 py-2 rounded-full bg-white/70 hover:bg-white border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ borderColor: "#3d2a14", color: "#3d2a14" }}
        >
          {isSaved ? (
            <BookmarkCheck className="w-4 h-4" strokeWidth={2.2} />
          ) : (
            <Bookmark className="w-4 h-4" strokeWidth={2.2} />
          )}
          <span className="text-xs font-semibold tracking-wide">
            {isSaved ? "Saved" : "Save"}
          </span>
        </button>

        <div className="px-7 md:px-10 py-9 md:py-10 pr-24 md:pr-28">
          <div className="flex items-center gap-2 mb-5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: yarn.accent }}
              aria-hidden
            />
            <span
              className="text-xs uppercase tracking-[0.18em] font-medium"
              style={{ color: yarn.accent }}
            >
              {yarn.label} · {yarn.promise}
            </span>
          </div>

          <p
            className="text-xl md:text-2xl leading-relaxed font-normal text-[#1a1a1a]"
            style={{ fontFamily: "Rubik, sans-serif" }}
            data-testid="output-card-text"
          >
            {card.yarn === "reflect" ? `“${card.text}”` : card.text}
          </p>

          {card.attribution && (
            <p
              className="mt-5 text-sm font-light tracking-wide text-[#3a3a3a]/85"
              data-testid="output-card-attribution"
            >
              — {card.attribution}
            </p>
          )}

          {/* Confirmation pulse when saved */}
          <AnimatePresence>
            {justSaved && (
              <motion.p
                key="saved-pulse"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-4 text-sm italic text-[#3a3a3a]/80"
                data-testid="output-card-saved-pulse"
              >
                Pinned to your loomboard.
              </motion.p>
            )}
          </AnimatePresence>

          <div className="mt-7 pt-5 border-t border-black/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p
              className="text-sm italic text-[#3a3a3a]/85 leading-relaxed"
              data-testid="output-card-meta"
            >
              {guestCapped
                ? "That's your three free threads. Join the 1% Club above for unlimited daily pulls."
                : dailyResting
                ? "Today's loom is woven. Fresh threads tomorrow — or pull once more if today calls for it."
                : dailyPullCount === 1
                ? "One thread drawn today."
                : "Two threads drawn today."}
            </p>
            <button
              type="button"
              onClick={handlePullAgainClick}
              data-testid="pull-again-button"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors shrink-0 border-2"
              style={{
                backgroundColor: yarn.deep,
                borderColor: "#3d2a14",
                color: "#3d2a14",
              }}
            >
              {guestCapped
                ? "Join to keep pulling"
                : dailyResting
                ? "Pull once more"
                : "Pull again"}
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
                <path
                  d="M2 7h10M8 3l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </motion.article>
    </AnimatePresence>
  );
}
